/**
 * Keeps the screen awake while a name sign is on display — a sign that blanks
 * out 30 seconds into the arrivals-hall wait is worse than no sign at all.
 *
 * Wake Lock is Chrome/Android and Safari 16.4+; everywhere else this is a
 * silent no-op. The lock is also re-acquired on visibilitychange because the
 * platform releases it whenever the page is backgrounded.
 */
import { useEffect } from 'react';

export default function useWakeLock(active = true) {
  useEffect(() => {
    if (!active) return undefined;

    const wakeLock = typeof navigator !== 'undefined' ? navigator.wakeLock : null;
    if (!wakeLock || typeof wakeLock.request !== 'function') return undefined;

    let sentinel = null;
    let released = false;

    const release = () => {
      try {
        if (sentinel && typeof sentinel.release === 'function') sentinel.release();
      } catch {
        // Already gone — nothing to do.
      }
      sentinel = null;
    };

    const acquire = async () => {
      if (released || sentinel) return;
      try {
        const next = await wakeLock.request('screen');
        if (released) {
          if (next && typeof next.release === 'function') next.release();
          return;
        }
        sentinel = next;
        if (sentinel && typeof sentinel.addEventListener === 'function') {
          sentinel.addEventListener('release', () => { sentinel = null; });
        }
      } catch {
        // Denied (low battery, no user gesture, unsupported) — degrade silently.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      released = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      release();
    };
  }, [active]);
}
